'use client'

import { type EBorrowCategory, isLST, isStablecoin } from '@/config/euler-contracts'
import { CLUSTER_KEY_BY_LABEL, E_BORROW_CATEGORIES } from '@/config/market-config'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { usePooledRoutes } from '@/features/euler-quick-borrow/hooks/usePooledRoutes'
import { usePortfolioEvmBalances } from '@/features/portfolio'
import { Typography } from '@/ui/atoms/typography/Typography'
import { useBreakpoint } from '@/ui/utils/useBreakpoint'
import { normalizeForSearch } from '@/utils/strings'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { formatUnits } from 'viem'
import { useAccount } from 'wagmi'
import { type BorrowPair, useBorrowPairs } from '../logic/useBorrowPairs'
import { BORROW_VAULT_CHAIN_ID } from '../logic/useVaultList'
import {
  type BorrowSortDirection,
  type BorrowSortField,
  type EBorrowFilter,
  EBorrowFilterBar,
} from './EBorrowFilterBar'
import { EBorrowListView } from './EBorrowListView'
import { VirtualBorrowTable } from './VirtualBorrowTable'
import { EBorrowSkeleton } from './skeleton/EBorrowSkeleton'

interface ExtendedBorrowPair extends BorrowPair {
  routeType?: 'euler' | 'pooled'
  clusterName?: string
  /** Numeric wallet balance for display and sorting */
  walletBalanceAmount?: number
}

function sortBorrowPairs(
  pairs: ExtendedBorrowPair[],
  sortBy: BorrowSortField,
  sortDirection: BorrowSortDirection,
  showLeverageMetrics: boolean,
): ExtendedBorrowPair[] {
  return [...pairs].sort((a, b) => {
    // User positions always come first (except in leverage mode where ROE ordering takes priority)
    if (!showLeverageMetrics) {
      const aHasBorrow = a.userBorrowAmount !== undefined && a.userBorrowAmount > 0
      const bHasBorrow = b.userBorrowAmount !== undefined && b.userBorrowAmount > 0
      if (aHasBorrow && !bHasBorrow) return -1
      if (!aHasBorrow && bHasBorrow) return 1
    }

    // Then sort by selected field
    let aValue: number
    let bValue: number

    switch (sortBy) {
      case 'rank': {
        // In leverage mode with default sort, use precomputed ROE as the ranking metric
        if (showLeverageMetrics) {
          const aRoe = a.maxRoe
          const bRoe = b.maxRoe

          if (aRoe === undefined && bRoe === undefined) return 0
          if (aRoe === undefined) return 1
          if (bRoe === undefined) return -1

          // Positive ROE first, then by value descending
          if (aRoe >= 0 && bRoe < 0) return -1
          if (aRoe < 0 && bRoe >= 0) return 1
          return bRoe - aRoe
        }

        // Standard rank sorting (non-leverage mode)
        const TIER1_LIQUIDITY_THRESHOLD = 400_000

        const getMarketTier = (pair: ExtendedBorrowPair): number => {
          const liquidity = pair.liquidityUSD ?? 0
          return liquidity >= TIER1_LIQUIDITY_THRESHOLD ? 1 : 2
        }

        const isStable = (pair: ExtendedBorrowPair): boolean => {
          return pair.liabilitySymbol ? isStablecoin(pair.liabilitySymbol) : false
        }

        const aTier = getMarketTier(a)
        const bTier = getMarketTier(b)

        if (aTier !== bTier) {
          return aTier - bTier
        }

        const aIsStable = isStable(a)
        const bIsStable = isStable(b)

        if (aIsStable !== bIsStable) {
          return aIsStable ? -1 : 1
        }

        const aRate = a.borrowAPY ?? Number.MAX_VALUE
        const bRate = b.borrowAPY ?? Number.MAX_VALUE
        return aRate - bRate
      }
      case 'apy':
        // In leverage mode, sort by precomputed ROE instead of borrow APY
        if (showLeverageMetrics) {
          aValue = a.maxRoe ?? 0
          bValue = b.maxRoe ?? 0
        } else {
          aValue = a.borrowAPY ?? 0
          bValue = b.borrowAPY ?? 0
        }
        break
      case 'liquidity':
        aValue = a.liquidityUSD ?? 0
        bValue = b.liquidityUSD ?? 0
        break
      case 'ltv': {
        const aLtv = a.maxLTV ?? 0n
        const bLtv = b.maxLTV ?? 0n
        const ltvComparison = aLtv > bLtv ? 1 : aLtv < bLtv ? -1 : 0
        return sortDirection === 'desc' ? -ltvComparison : ltvComparison
      }
      case 'utilization':
        aValue = a.utilization ?? 0
        bValue = b.utilization ?? 0
        break
    }

    return sortDirection === 'desc' ? bValue - aValue : aValue - bValue
  })
}

interface BorrowExploreState {
  filter: EBorrowFilter
  showLeverageMetrics: boolean
}

const VALID_E_BORROW_CATEGORIES: ReadonlySet<string> = new Set(
  E_BORROW_CATEGORIES.filter((category): category is EBorrowCategory => category !== 'All'),
)

function parseBorrowExploreState(searchParams: URLSearchParams): BorrowExploreState {
  const categoriesParam = searchParams.get('markets')
  const collateralParam = searchParams.get('collateral')
  const debtParam = searchParams.get('debt')
  const inWalletParam = searchParams.get('inWallet')
  const openPositionsParam = searchParams.get('openPositions')
  const searchParam = searchParams.get('q')
  const sortByParam = searchParams.get('sortBy') as BorrowSortField | null
  const sortDirParam = searchParams.get('sortDir') as BorrowSortDirection | null
  const leverageParam = searchParams.get('strategies')

  const categories = categoriesParam
    ? categoriesParam
        .split(',')
        .filter((category): category is EBorrowCategory => VALID_E_BORROW_CATEGORIES.has(category))
    : []

  return {
    filter: {
      categories,
      searchQuery: searchParam || '',
      collateralFilter: collateralParam ? collateralParam.split(',').filter(Boolean) : [],
      debtFilter: debtParam ? debtParam.split(',').filter(Boolean) : [],
      inWalletOnly: inWalletParam === 'true',
      openPositionsOnly: openPositionsParam === 'true',
      sortBy:
        sortByParam && ['rank', 'apy', 'liquidity', 'utilization', 'ltv'].includes(sortByParam) ? sortByParam : 'rank',
      sortDirection: sortDirParam === 'asc' ? 'asc' : 'desc',
    },
    showLeverageMetrics: leverageParam === 'true',
  }
}

function buildBorrowExploreSearchParams(filter: EBorrowFilter, showLeverageMetrics: boolean): string {
  const params = new URLSearchParams()

  if (filter.categories.length > 0) {
    params.set('markets', filter.categories.join(','))
  }
  if (filter.collateralFilter.length > 0) {
    params.set('collateral', filter.collateralFilter.join(','))
  }
  if (filter.debtFilter.length > 0) {
    params.set('debt', filter.debtFilter.join(','))
  }
  if (filter.inWalletOnly) {
    params.set('inWallet', 'true')
  }
  if (filter.openPositionsOnly) {
    params.set('openPositions', 'true')
  }
  if (filter.searchQuery) {
    params.set('q', filter.searchQuery)
  }
  if (filter.sortBy !== 'rank') {
    params.set('sortBy', filter.sortBy)
  }
  if (filter.sortDirection !== 'desc') {
    params.set('sortDir', filter.sortDirection)
  }
  if (showLeverageMetrics) {
    params.set('strategies', 'true')
  }

  return params.toString()
}

export function EBorrow() {
  return (
    <Suspense fallback={<EBorrowSkeleton />}>
      <EBorrowContent />
    </Suspense>
  )
}

function EBorrowContent() {
  const { address } = useAccount()
  const searchParams = useSearchParams()
  const { pairs: eulerPairs, isLoading: isEulerLoading } = useBorrowPairs(address)
  const { routes: pooledRoutes, isLoading: isPooledLoading } = usePooledRoutes()
  const { balances: walletBalances } = usePortfolioEvmBalances({ chainId: BORROW_VAULT_CHAIN_ID })
  const [initialBorrowExploreState] = useState<BorrowExploreState>(() => parseBorrowExploreState(searchParams))
  const [filter, setFilter] = useState<EBorrowFilter>(initialBorrowExploreState.filter)
  const [showLeverageMetrics, setShowLeverageMetrics] = useState(initialBorrowExploreState.showLeverageMetrics)
  const isDesktop = useBreakpoint('lg')
  const router = useRouter()
  const pathname = usePathname()
  const lastSyncedQueryRef = useRef<string | null>(null)

  useEffect(() => {
    const nextState = parseBorrowExploreState(searchParams)
    const canonicalQueryString = buildBorrowExploreSearchParams(nextState.filter, nextState.showLeverageMetrics)

    if (lastSyncedQueryRef.current === canonicalQueryString) {
      return
    }

    lastSyncedQueryRef.current = canonicalQueryString
    setFilter(nextState.filter)
    setShowLeverageMetrics(nextState.showLeverageMetrics)
  }, [searchParams])

  useEffect(() => {
    const nextQueryString = buildBorrowExploreSearchParams(filter, showLeverageMetrics)

    if (lastSyncedQueryRef.current === nextQueryString) {
      return
    }

    lastSyncedQueryRef.current = nextQueryString
    const newUrl = nextQueryString ? `${pathname}?${nextQueryString}` : pathname
    router.replace(newUrl, { scroll: false })
  }, [filter, showLeverageMetrics, router, pathname])

  // Reset wallet-related filters when wallet disconnects
  useEffect(() => {
    if (!address) {
      setFilter((prev) => ({
        ...prev,
        inWalletOnly: false,
        openPositionsOnly: false,
      }))
    }
  }, [address])

  const pairs: ExtendedBorrowPair[] = useMemo(() => {
    const toWalletBalanceAmount = (balance: bigint | undefined, decimals: number | undefined): number | undefined => {
      if (balance === undefined || balance === 0n) return undefined
      return decimals !== undefined ? Number(formatUnits(balance, decimals)) : undefined
    }

    const eulerWithType: ExtendedBorrowPair[] = eulerPairs.map((p) => ({
      ...p,
      routeType: 'euler' as const,
      walletBalanceAmount: toWalletBalanceAmount(p.collateralBalance, p.collateralDecimals),
    }))

    const pooledAsPairs: ExtendedBorrowPair[] = pooledRoutes.map((route) => {
      const wb = route.collateralSymbol
        ? walletBalances.find((w) => w.token.symbol === TokenSymbol(route.collateralSymbol!))
        : undefined
      const walletBalance = wb?.balance

      return {
        collateral: route.collateral,
        liability: route.liability,
        collateralSymbol: route.collateralSymbol,
        liabilitySymbol: route.liabilitySymbol,
        maxLTV: route.maxLTV,
        liquidationLTV: route.liquidationLTV,
        borrowAPY: route.borrowAPY,
        liquidityUSD: route.liquidityUSD,
        utilization: route.utilization,
        hasPosition: route.hasPosition,
        userBorrowAmount: route.userBorrowAmount,
        userBorrowAmountUsd: route.userBorrowAmountUsd,
        userCollateralDeposit: route.userCollateralDeposit,
        userCollateralDepositUsd: route.userCollateralDepositUsd,
        healthFactor: route.healthFactor,
        walletBalanceAmount: walletBalance?.gt(0) ? walletBalance.toNumber() : undefined,
        routeType: 'pooled' as const,
        clusterName: 'HypurrFi Pooled',
      }
    })

    return [...eulerWithType, ...pooledAsPairs]
  }, [eulerPairs, pooledRoutes, walletBalances])

  const { collateralOptions, debtOptions, collateralAddressBySymbol, debtAddressBySymbol } = useMemo(() => {
    const collateralSet = new Set<string>()
    const debtSet = new Set<string>()
    const collateralAddressBySymbol: Record<string, string> = {}
    const debtAddressBySymbol: Record<string, string> = {}
    for (const pair of pairs) {
      if (pair.collateralSymbol) {
        collateralSet.add(pair.collateralSymbol)
        const address = pair.collateralUnderlying || pair.collateral
        if (!collateralAddressBySymbol[pair.collateralSymbol] && address) {
          collateralAddressBySymbol[pair.collateralSymbol] = address
        }
      }
      if (pair.liabilitySymbol) {
        debtSet.add(pair.liabilitySymbol)
        const address = pair.liabilityUnderlying || pair.liability
        if (!debtAddressBySymbol[pair.liabilitySymbol] && address) {
          debtAddressBySymbol[pair.liabilitySymbol] = address
        }
      }
    }
    return {
      collateralOptions: Array.from(collateralSet).sort(),
      debtOptions: Array.from(debtSet).sort(),
      collateralAddressBySymbol,
      debtAddressBySymbol,
    }
  }, [pairs])

  const isLoading = isEulerLoading || isPooledLoading
  const hasLoadedPairs = pairs.some((p) => p.collateralSymbol && p.liabilitySymbol)

  if (isLoading || (pairs.length > 0 && !hasLoadedPairs)) {
    return <EBorrowSkeleton />
  }

  const filteredPairs = pairs.filter((pair) => {
    if (!pair.collateralSymbol || !pair.liabilitySymbol) {
      return false
    }

    if (showLeverageMetrics && pair.routeType === 'pooled') {
      return false
    }

    const normalizedSearchQuery = normalizeForSearch(filter.searchQuery)

    const matchesSearch =
      filter.searchQuery === '' ||
      normalizeForSearch(pair.collateralSymbol).includes(normalizedSearchQuery) ||
      normalizeForSearch(pair.liabilitySymbol).includes(normalizedSearchQuery)

    const matchesCategory = matchesCategoryFilter(pair, filter.categories ?? [])
    const matchesWallet =
      !filter.inWalletOnly || (pair.walletBalanceAmount !== undefined && pair.walletBalanceAmount > 0)
    const DUST_THRESHOLD_USD = 1.0
    const hasCollateralPosition =
      pair.userCollateralDepositUsd !== undefined && pair.userCollateralDepositUsd >= DUST_THRESHOLD_USD
    const hasDebtPosition = pair.userBorrowAmountUsd !== undefined && pair.userBorrowAmountUsd >= DUST_THRESHOLD_USD
    const matchesPosition = !filter.openPositionsOnly || (hasCollateralPosition && hasDebtPosition)
    const matchesCollateral =
      filter.collateralFilter.length === 0 || filter.collateralFilter.includes(pair.collateralSymbol)
    const matchesDebt = filter.debtFilter.length === 0 || filter.debtFilter.includes(pair.liabilitySymbol)

    return matchesSearch && matchesCategory && matchesWallet && matchesPosition && matchesCollateral && matchesDebt
  })

  const sortedPairs = sortBorrowPairs(filteredPairs, filter.sortBy, filter.sortDirection, showLeverageMetrics)

  return (
    <div className="flex flex-col gap-6">
      <EBorrowFilterBar
        filter={filter}
        onFilterChange={setFilter}
        hasWallet={!!address}
        showLeverageMetrics={showLeverageMetrics}
        onShowLeverageMetricsChange={setShowLeverageMetrics}
        collateralOptions={collateralOptions}
        debtOptions={debtOptions}
        collateralAddressBySymbol={collateralAddressBySymbol}
        debtAddressBySymbol={debtAddressBySymbol}
      />

      {sortedPairs.length === 0 ? (
        <div className="flex items-center justify-center p-8">
          <Typography variant="p" className="text-muted-foreground">
            No borrow pairs found
          </Typography>
        </div>
      ) : isDesktop ? (
        <VirtualBorrowTable
          pairs={sortedPairs}
          showBorrowed={filter.openPositionsOnly}
          showInWallet={filter.inWalletOnly}
          showLeverageMetrics={showLeverageMetrics}
        />
      ) : (
        <div className="mx-auto w-full max-w-lg">
          <EBorrowListView pairs={sortedPairs} showLeverageMetrics={showLeverageMetrics} />
        </div>
      )}
    </div>
  )
}

function matchesCategoryFilter(pair: ExtendedBorrowPair, categories: EBorrowCategory[]): boolean {
  if (categories.length === 0) return true

  return categories.some((category) => {
    if (category === 'All') return true
    if (category === 'Stablecoins') {
      return isStablecoin(pair.liabilitySymbol ?? '') || isStablecoin(pair.collateralSymbol ?? '')
    }
    if (category === 'LST') {
      return isLST(pair.collateralSymbol ?? '') || isLST(pair.liabilitySymbol ?? '')
    }
    if (category === 'Volatile') {
      return !isStablecoin(pair.collateralSymbol ?? '') && !isLST(pair.collateralSymbol ?? '')
    }

    const clusterKey = CLUSTER_KEY_BY_LABEL.get(category)
    if (!clusterKey) return false

    // Synthetic pooled cluster → distinguished by routeType, not market.key.
    if (clusterKey === 'pooled') return pair.routeType === 'pooled'

    // Product-backed elend cluster → match on either side's market.key.
    const marketKey = `hypurrfi-${clusterKey}`
    return (
      pair.routeType !== 'pooled' &&
      (pair.collateralMarket?.key === marketKey || pair.liabilityMarket?.key === marketKey)
    )
  })
}
